import type { PeriodKey } from "@easyshop/shared";
import { ymd } from "@/components/planning/utils";

/**
 * Sélection de période partagée par l'accueil, la fiche boutique et les ventes.
 *
 * - Préréglage (Jour / Semaine / Mois / Année) ancré sur un jour (`date`,
 *   AAAA-MM-JJ) : le jour même, ou la semaine / le mois / l'année qui le
 *   contient. `null` = la période en cours, qui seule se rafraîchit en direct ;
 *   on y revient dès que l'ancre y retombe, et jamais au-delà (pas de futur).
 * - « Période… » (`perso`) : plage libre, bornes incluses.
 *
 * La comparaison est « prev » (période d'avant) ou « n1 » (l'an dernier). Les
 * dates comparées suivent les mêmes règles que l'API (lib/periods.ts) :
 * J-7 / J-364 pour un jour, -7 j / -364 j pour une semaine, etc.
 */

export type PeriodMode = PeriodKey | "perso";
export type CmpMode = "prev" | "n1";
/** Plage de jours, bornes incluses (AAAA-MM-JJ). */
export interface DayRange {
  from: string;
  to: string;
}
export interface PeriodSel {
  period: PeriodMode;
  date: string | null;
  range: DayRange | null;
  cmp: CmpMode;
}

export const parseDay = (s: string) => {
  const [y, m, d] = s.split("-").map(Number);
  return new Date(y, m - 1, d);
};
export const addDays = (d: Date, n: number) => new Date(d.getFullYear(), d.getMonth(), d.getDate() + n);
export const startOfWeek = (d: Date) => addDays(d, -((d.getDay() + 6) % 7));
const today = () => {
  const n = new Date();
  return new Date(n.getFullYear(), n.getMonth(), n.getDate());
};
const daysBetween = (a: Date, b: Date) => Math.round((b.getTime() - a.getTime()) / 86_400_000);

function samePeriod(period: PeriodKey, a: Date, b: Date): boolean {
  switch (period) {
    case "jour":
      return ymd(a) === ymd(b);
    case "semaine":
      return ymd(startOfWeek(a)) === ymd(startOfWeek(b));
    case "mois":
      return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth();
    case "annee":
      return a.getFullYear() === b.getFullYear();
  }
}

/** L'ancre à garder pour `d` : `null` s'il tombe dans la période en cours (ou après). */
export function normalizeAnchor(period: PeriodKey, d: Date | string | null): string | null {
  if (d == null) return null;
  const day = typeof d === "string" ? parseDay(d) : d;
  const now = new Date();
  if (day > now || samePeriod(period, day, now)) return null;
  return ymd(day);
}

/** Plage affichée, bornes incluses (la période en cours va jusqu'à sa fin). */
export function selRange(s: Pick<PeriodSel, "period" | "date" | "range">): { from: Date; to: Date } {
  const base = s.date ? parseDay(s.date) : today();
  switch (s.period) {
    case "jour":
      return { from: base, to: base };
    case "semaine": {
      const from = startOfWeek(base);
      return { from, to: addDays(from, 6) };
    }
    case "mois":
      return { from: new Date(base.getFullYear(), base.getMonth(), 1), to: new Date(base.getFullYear(), base.getMonth() + 1, 0) };
    case "annee":
      return { from: new Date(base.getFullYear(), 0, 1), to: new Date(base.getFullYear(), 11, 31) };
    case "perso":
      return s.range ? { from: parseDay(s.range.from), to: parseDay(s.range.to) } : { from: base, to: base };
  }
}

/** Dates comparées pour un choix de comparaison (mêmes règles que l'API). */
export function cmpRange(s: Pick<PeriodSel, "period" | "date" | "range">, cmp: CmpMode): { from: Date; to: Date } {
  const r = selRange(s);
  const shift = (n: number) => ({ from: addDays(r.from, n), to: addDays(r.to, n) });
  switch (s.period) {
    case "jour":
      return shift(cmp === "n1" ? -364 : -7);
    case "semaine":
      return shift(cmp === "n1" ? -364 : -7);
    case "mois":
      return cmp === "n1"
        ? { from: new Date(r.from.getFullYear() - 1, r.from.getMonth(), 1), to: new Date(r.from.getFullYear() - 1, r.from.getMonth() + 1, 0) }
        : { from: new Date(r.from.getFullYear(), r.from.getMonth() - 1, 1), to: new Date(r.from.getFullYear(), r.from.getMonth(), 0) };
    case "annee":
      return { from: new Date(r.from.getFullYear() - 1, 0, 1), to: new Date(r.from.getFullYear() - 1, 11, 31) };
    case "perso": {
      if (cmp === "n1") return shift(-364);
      const days = daysBetween(r.from, r.to) + 1;
      return shift(-days);
    }
  }
}

/** Choix de comparaison proposés pour une période (un seul pour l'année). */
export function cmpChoices(s: Pick<PeriodSel, "period" | "date" | "range">): { key: CmpMode; title: string }[] {
  switch (s.period) {
    case "jour":
      return [
        { key: "prev", title: "Même jour, semaine dernière" },
        { key: "n1", title: "Même jour, l'an dernier" },
      ];
    case "semaine":
      return [
        { key: "prev", title: "Semaine précédente" },
        { key: "n1", title: "Même semaine, l'an dernier" },
      ];
    case "mois":
      return [
        { key: "prev", title: "Mois précédent" },
        { key: "n1", title: "Même mois, l'an dernier" },
      ];
    case "annee":
      return [{ key: "prev", title: "Année précédente" }];
    case "perso": {
      const r = selRange(s);
      const days = daysBetween(r.from, r.to) + 1;
      return [
        { key: "prev", title: days === 1 ? "Le jour d'avant" : `Les ${days} jours d'avant` },
        { key: "n1", title: "Mêmes dates, l'an dernier" },
      ];
    }
  }
}

export function isoWeek(d: Date): number {
  const t = new Date(d.getFullYear(), d.getMonth(), d.getDate() + 3 - ((d.getDay() + 6) % 7));
  const firstThursday = new Date(t.getFullYear(), 0, 4);
  return 1 + Math.round(((t.getTime() - firstThursday.getTime()) / 86_400_000 - 3 + ((firstThursday.getDay() + 6) % 7)) / 7);
}

const cap = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);
const fmt = (d: Date, o: Intl.DateTimeFormatOptions) => d.toLocaleDateString("fr-FR", o);
/** Année ajoutée seulement si elle diffère de l'année en cours. */
const withYear = (d: Date) => (d.getFullYear() !== new Date().getFullYear() ? ` ${d.getFullYear()}` : "");
const dayLong = (d: Date) => fmt(d, { weekday: "long", day: "numeric", month: "long" }) + withYear(d);
const dayShort = (d: Date) => fmt(d, { day: "numeric", month: "short" });

/** « 5 – 11 oct. » ou « 28 sept. – 4 oct. ». */
function spanLabel(from: Date, to: Date, sep = " – "): string {
  const same = from.getMonth() === to.getMonth() && from.getFullYear() === to.getFullYear();
  return `${same ? from.getDate() : dayShort(from)}${sep}${dayShort(to)}${withYear(to)}`;
}

/** Libellé d'une plage selon le type de période (sans « Aujourd'hui »). */
export function rangeLabel(period: PeriodMode, r: { from: Date; to: Date }): string {
  switch (period) {
    case "jour":
      return dayLong(r.from);
    case "semaine":
      return `S${isoWeek(r.from)} · ${spanLabel(r.from, r.to)}`;
    case "mois":
      return cap(fmt(r.from, { month: "long", year: "numeric" }));
    case "annee":
      return String(r.from.getFullYear());
    case "perso":
      return ymd(r.from) === ymd(r.to) ? dayLong(r.from) : `${dayShort(r.from)}${withYear(r.from)} → ${dayShort(r.to)}${withYear(r.to)}`;
  }
}

/** « Aujourd'hui · mardi 6 octobre », « S41 · 5 – 11 oct. », « Octobre 2026 », « 2026 », « 12 sept. → 6 oct. ». */
export function periodLabel(s: Pick<PeriodSel, "period" | "date" | "range">): string {
  const r = selRange(s);
  if (s.period === "jour") {
    const t = today();
    if (ymd(r.from) === ymd(t)) return `Aujourd'hui · ${dayLong(r.from)}`;
    if (ymd(r.from) === ymd(addDays(t, -1))) return `Hier · ${dayLong(r.from)}`;
  }
  return rangeLabel(s.period, r);
}

/** Dates de la comparaison active, au format du type de période. */
export function compareLabel(s: PeriodSel): string {
  return rangeLabel(s.period, cmpRange(s, s.cmp));
}

/** « mer. 30 sept. » (année ajoutée si elle diffère de l'année en cours). */
const dayAbbr = (d: Date) => fmt(d, { weekday: "short", day: "numeric", month: "short" }) + withYear(d);

/**
 * Période comparée en bref, devant un montant : « mer. dernier », « S40 »,
 * « sept. », « 2025 » ; en N-1 : « mer. 8 oct. 2025 », « S41 2025 », « oct. 2025 ».
 * « mer. dernier » seulement pour aujourd'hui : un jour passé garde sa date.
 */
export function cmpShortLabel(s: PeriodSel): string {
  const r = cmpRange(s, s.cmp);
  const n1 = s.cmp === "n1";
  switch (s.period) {
    case "jour":
      return !n1 && s.date == null ? `${fmt(r.from, { weekday: "short" })} dernier` : dayAbbr(r.from);
    case "semaine":
      return `S${isoWeek(r.from)}${withYear(r.from)}`;
    case "mois":
      return fmt(r.from, { month: "short" }) + withYear(r.from);
    case "annee":
      return String(r.from.getFullYear());
    case "perso":
      return ymd(r.from) === ymd(r.to) ? dayAbbr(r.from) : `${dayShort(r.from)} → ${dayShort(r.to)}${withYear(r.to)}`;
  }
}

/** La période affichée en bref, en regard de `cmpShortLabel` : « S41 », « oct. », « 2026 ». */
export function periodShortLabel(s: Pick<PeriodSel, "period" | "date" | "range">): string {
  const r = selRange(s);
  switch (s.period) {
    case "jour":
      return ymd(r.from) === ymd(today()) ? "Aujourd'hui" : dayAbbr(r.from);
    case "semaine":
      return `S${isoWeek(r.from)}${withYear(r.from)}`;
    case "mois":
      return cap(fmt(r.from, { month: "short" })) + withYear(r.from);
    case "annee":
      return String(r.from.getFullYear());
    case "perso":
      return ymd(r.from) === ymd(r.to) ? dayAbbr(r.from) : `${dayShort(r.from)} → ${dayShort(r.to)}${withYear(r.to)}`;
  }
}

/** Période comparée sous le grand chiffre : « mer. 30 sept. », sinon comme `compareLabel`. */
export function cmpDateLabel(s: PeriodSel): string {
  return s.period === "jour" ? dayAbbr(cmpRange(s, s.cmp).from) : compareLabel(s);
}

/** La période précédente (-1) ou suivante (+1), jamais dans le futur. */
export function shiftSel(s: PeriodSel, dir: -1 | 1): Pick<PeriodSel, "date" | "range"> {
  if (s.period === "perso") {
    const r = selRange(s);
    const days = daysBetween(r.from, r.to) + 1;
    const from = addDays(r.from, days * dir);
    const to = addDays(r.to, days * dir);
    return { date: s.date, range: { from: ymd(from), to: ymd(to) } };
  }
  const base = s.date ? parseDay(s.date) : new Date();
  const next =
    s.period === "jour"
      ? addDays(base, dir)
      : s.period === "semaine"
        ? addDays(base, 7 * dir)
        : s.period === "mois"
          ? new Date(base.getFullYear(), base.getMonth() + dir, 1)
          : new Date(base.getFullYear() + dir, 0, 1);
  return { date: normalizeAnchor(s.period, next), range: s.range };
}

/** Faux si la période suivante commencerait après aujourd'hui. */
export function canShiftNext(s: PeriodSel): boolean {
  if (s.period === "perso") return addDays(selRange(s).to, 1) <= today();
  return s.date != null;
}

/** Raccourcis du calendrier. */
export const SHORTCUTS: { label: string; sel: () => Pick<PeriodSel, "period" | "date" | "range"> }[] = [
  { label: "Aujourd'hui", sel: () => ({ period: "jour", date: null, range: null }) },
  { label: "Hier", sel: () => ({ period: "jour", date: ymd(addDays(today(), -1)), range: null }) },
  { label: "Cette semaine", sel: () => ({ period: "semaine", date: null, range: null }) },
  { label: "Semaine dernière", sel: () => ({ period: "semaine", date: normalizeAnchor("semaine", addDays(today(), -7)), range: null }) },
  { label: "Ce mois-ci", sel: () => ({ period: "mois", date: null, range: null }) },
  {
    label: "Mois dernier",
    sel: () => ({ period: "mois", date: normalizeAnchor("mois", new Date(today().getFullYear(), today().getMonth() - 1, 1)), range: null }),
  },
  { label: "30 derniers jours", sel: () => ({ period: "perso", date: null, range: { from: ymd(addDays(today(), -29)), to: ymd(today()) } }) },
  { label: "Cette année", sel: () => ({ period: "annee", date: null, range: null }) },
];

/** Vrai si la sélection courante correspond au raccourci. */
export function isShortcut(s: PeriodSel, sc: (typeof SHORTCUTS)[number]): boolean {
  const t = sc.sel();
  if (t.period !== s.period) return false;
  if (t.period === "perso") return t.range?.from === s.range?.from && t.range?.to === s.range?.to;
  return t.date === s.date;
}

/** Paramètres d'API de la sélection (plage libre : from/to + éventuelle comparaison N-1). */
export function apiParams(s: PeriodSel): Record<string, string> {
  if (s.period === "perso" && s.range) {
    const p: Record<string, string> = { from: s.range.from, to: s.range.to };
    if (s.cmp === "n1") {
      const c = cmpRange(s, "n1");
      p.cmpFrom = ymd(c.from);
      p.cmpTo = ymd(c.to);
    }
    return p;
  }
  const period = s.period === "perso" ? "jour" : s.period;
  // « prev » : J-7 pour un jour, période précédente ailleurs (défaut de l'API).
  const p: Record<string, string> = { period, cmp: s.cmp === "n1" && period !== "annee" ? "n1" : "j7" };
  if (s.date) p.date = s.date;
  return p;
}
